import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { App } from './App';
import { Popout } from './PopoutView';
import { BriefingView } from './BriefingView';
import { WallView } from './WallView';
import { PanelView } from './PanelView';
import { SubscribeView } from './SubscribeView';
import { AuthProvider } from './auth/AuthProvider';
import { AuthGate } from './auth/AuthGate';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

// Lightweight path routing (no router dependency — Vite's SPA fallback serves
// index.html for every path, so the browser sees the pathname in JS):
//   /popout         → the bare multi-monitor camera viewer.
//   /briefing/<tok> → the B.1 read-only Briefing-Up view for the Minister's
//                     office (token-gated, no auth, no actions, no audit).
//   ?wall=<config> → the WALL composed video-wall (#188/#221/#269) — a
//                     read-only, config-driven multi-zone wall. `?wall=1`
//                     (legacy) = GOC; `?wall=goc[&flankRows=2]` = GOC;
//                     `?wall=peoc[&panels=N]` = the PEOC strip.
//   /panel          → the operations panel (#197) — protected queues + pinned
//                     media for a second monitor, synced to the COP.
//   everything else → the main operator map app.
const path = window.location.pathname.replace(/\/$/, '');
const isPopout = path === '/popout';
const isPanel = path === '/panel';
// /subscribe → the #547 PUBLIC resident self-opt-in page (no COP chrome, no Cesium; the
// AuthGate is bypassed like the other display/public contexts below).
const isSubscribe = path === '/subscribe';
const search = new URLSearchParams(window.location.search);
const wallParam = search.get('wall');
const panelsParam = search.get('panels');
const wallPanels = panelsParam != null && panelsParam !== '' ? Number(panelsParam) : null;
const flankRowsParam = search.get('flankRows');
const wallFlankRows =
  flankRowsParam != null && flankRowsParam !== '' ? Number(flankRowsParam) : null;
// `?viewport=WxH` (wall QA only) — pins the wall's measured size to the
// declared room aspect so the founder replication recipe is deterministic on
// displays that clamp window resizes (and in headless/occluded QA captures,
// where ResizeObserver never delivers). Production walls omit it and measure.
const viewportParam = search.get('viewport');
const viewportMatch = viewportParam?.match(/^(\d+)x(\d+)$/);
const wallViewport = viewportMatch
  ? { width: Number(viewportMatch[1]), height: Number(viewportMatch[2]) }
  : undefined;
const dwellParam = search.get('dwell');
const wallDwellMs = dwellParam != null && dwellParam !== '' ? Number(dwellParam) : null;
// `?tour=0` — the hourly risk-tour kill-switch (#272, PART XI Ruling 2.1).
const tourParam = search.get('tour');
const wallTourEnabled = !(tourParam === '0' || tourParam === 'false');
// `?tourMinute=NN` (wall QA only) — pins the tour clock's minute-of-hour so
// verification can force any stop without waiting for :45.
const tourMinuteParam = search.get('tourMinute');
const wallTourMinute =
  tourMinuteParam != null && tourMinuteParam !== '' && Number.isFinite(Number(tourMinuteParam))
    ? Number(tourMinuteParam)
    : null;
// `?radar=1` (wall QA only) — the Ruling 1.4e founder-gate override: shows the
// GeoMet radar overlay for the screenshot acceptance pass. Radar's ambient
// default stays OFF until the founder approves on glass.
const radarParam = search.get('radar');
const wallRadarGate = radarParam === '1' || radarParam === 'true';
// `?blowup=N` (wall QA only) — overrides the tile blow-up auto-restore window
// (ms; #273 PART XI Ruling 4). Production walls use the ~5-min default.
const blowupParam = search.get('blowup');
const wallBlowupMs =
  blowupParam != null && blowupParam !== '' && Number.isFinite(Number(blowupParam))
    ? Number(blowupParam)
    : null;
const isWall = wallParam != null && wallParam !== '' && wallParam !== '0' && wallParam !== 'false';
const briefingMatch = path.match(/^\/briefing\/([A-Za-z0-9_-]{16,128})$/);

// Display / capability contexts that must boot WITHOUT an interactive login (RBAC
// R.2b gate bypass, #314): the bare popout camera viewer, the read-only video wall,
// the token-gated Briefing-Up view, the second-monitor ops panel, and the #547 public
// resident /subscribe page. Only the main COP (<App/>, the fall-through below) is gated;
// a bare `#cam=` deep-link on the COP is NOT a display context — it goes through the gate
// and the URL survives login.
const isDisplayContext = isPopout || isPanel || isWall || isSubscribe || Boolean(briefingMatch);

const root =
  isPopout ? (
    <Popout />
  ) : isPanel ? (
    <PanelView />
  ) : isSubscribe ? (
    <SubscribeView />
  ) : isWall ? (
    <WallView
      configName={wallParam}
      panels={wallPanels}
      flankRows={wallFlankRows}
      dwellMs={wallDwellMs}
      blowupRestoreMs={wallBlowupMs}
      viewport={wallViewport}
      tourEnabled={wallTourEnabled}
      tourMinute={wallTourMinute}
      radarGate={wallRadarGate}
    />
  ) : briefingMatch ? (
    <BriefingView token={briefingMatch[1]} readOnly />
  ) : (
    <App />
  );

createRoot(rootEl).render(
  <StrictMode>
    <AuthProvider>
      <AuthGate bypass={isDisplayContext}>{root}</AuthGate>
    </AuthProvider>
  </StrictMode>,
);
